import React from 'react';
import {
  Card, CardContent, CardActions, Typography, Button, Box, List, ListItem, ListItemIcon, ListItemText, Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';

function formatPrice(cents, currency = 'USD', interval = 'month') {
  const amount = (cents / 100).toFixed(2);
  if (cents === 0) return 'Free';
  return `$${amount}/${interval === 'year' ? 'yr' : 'mo'}`;
}

function formatFeatureValue(value) {
  if (value === false) return null;
  if (value === true) return true;
  if (value === -1) return 'Unlimited';
  if (Array.isArray(value)) {
    if (value.includes('all')) return 'All';
    return value.length > 0 ? `${value.length} options` : null;
  }
  if (typeof value === 'number') return String(value);
  return value;
}

function humanizeKey(key) {
  return key.split('.').pop().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function flattenFeatures(obj, prefix = '') {
  const result = [];
  for (const [key, value] of Object.entries(obj || {})) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result.push(...flattenFeatures(value, fullKey));
    } else {
      result.push([fullKey, value]);
    }
  }
  return result;
}

export default function PlanCard({ plan, action, actions, onSelect, disabled, catalog }) {
  const isCurrent = action?.type === 'current';
  const flatFeatures = flattenFeatures(plan.features);
  const cat = catalog || {};

  const sortedFeatures = flatFeatures
    .map(([key, value]) => ({
      key, value,
      entry: cat[key] || null,
      valueOrder: cat[key]?.value_order || 0
    }))
    .sort((a, b) => b.valueOrder - a.valueOrder);

  const displayFeatures = [];
  for (const { key, value, entry } of sortedFeatures) {
    const formatted = formatFeatureValue(value);
    if (formatted === null) continue;
    const label = entry?.display_name || humanizeKey(key);
    const description = entry?.description || null;
    if (formatted === true) {
      displayFeatures.push({ label, value: null, description });
    } else {
      displayFeatures.push({ label, value: String(formatted), description });
    }
  }

  // Use actions array if provided, otherwise fall back to singular action
  const allActions = actions?.length > 0 ? actions : (action ? [action] : []);

  return (
    <Card
      variant={isCurrent ? 'outlined' : 'elevation'}
      sx={{
        height: '100%', display: 'flex', flexDirection: 'column',
        border: isCurrent ? '2px solid' : undefined,
        borderColor: isCurrent ? 'primary.main' : undefined,
        position: 'relative'
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h5" gutterBottom>{plan.name}</Typography>
        <Typography variant="h4" color="primary" gutterBottom sx={{ fontWeight: 700 }}>
          {formatPrice(plan.price_cents, plan.currency, plan.interval_type)}
        </Typography>
        {plan.description && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {plan.description}
          </Typography>
        )}
        <List dense>
          {displayFeatures.map(({ label, value, description }) => (
            <Tooltip key={label} title={description || ''} placement="right" arrow>
              <ListItem disableGutters sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <CheckCircleIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={value ? `${label}: ${value}` : label}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </Tooltip>
          ))}
        </List>
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0, flexDirection: 'column', gap: 1 }}>
        {allActions.map((a, idx) => {
          const isPrimary = idx === 0 && !a.disabled;
          const buttonColor = a.style === 'inherit' ? 'inherit' : 'primary';
          const buttonVariant = a.disabled ? 'outlined' : isPrimary ? 'contained' : 'outlined';

          return (
            <Button
              key={a.id || idx}
              fullWidth
              variant={buttonVariant}
              color={buttonColor}
              onClick={() => onSelect(plan, a)}
              disabled={disabled || a.disabled}
              startIcon={a.type?.includes('upgrade') && isPrimary ? <StarIcon /> : null}
              sx={!isPrimary && !a.disabled ? { fontSize: '0.8rem' } : undefined}
            >
              {a.label || 'Select'}
            </Button>
          );
        })}
      </CardActions>
    </Card>
  );
}
